/**
 * WHICH ONE PART TO PUT ON THE QUOTE.
 *
 * Anthony: "put only one part option in the line item for the part.
 * Oreillys gets priority but Autozone is fine if the information is
 * better. let agents click view more or something to see more."
 *
 * The counter lookup returns up to three listings from each of three
 * suppliers, so asking for a serpentine belt gave nine near-identical rows
 * and the decision landed on whoever happened to be pricing the job. An
 * advisor does not want a shopping list. They want the part, and a way to
 * disagree with it.
 *
 * ─── WHAT MAKES ONE BETTER ─────────────────────────────────────────────
 *
 * A PART NUMBER FIRST. It is the thing a counter can pull off a shelf and
 * the thing that ends up on the invoice; a listing without one is a guess
 * with a price attached. Then a brand, for the same reason one step down.
 *
 * ONLY THEN THE SUPPLIER, and there O'Reilly wins. It is the house
 * account, the rep is known, and the San Jacinto store is 76% of what this
 * shop spends. Cheapest breaks whatever is left.
 *
 * Which means AutoZone beats O'Reilly exactly when AutoZone has the part
 * number and O'Reilly does not. That is the rule as it was given, and it
 * is here rather than inline in a page because it decides what the shop
 * buys, which makes it worth testing.
 */

export type Counter = {
  title: string;
  brand: string;
  partNumber: string;
  price: number;
  supplier: string;
  /** 0 is O'Reilly, 1 AutoZone, 2 NAPA. Anything else is not a counter. */
  supplierRank?: number;
};

/** How much this listing actually tells you. */
function known(r: Counter): number {
  return (r.partNumber ? 2 : 0) + (r.brand ? 1 : 0);
}

/**
 * The one to use, and the rest in the order somebody would consider them.
 * An empty list gives a null best rather than throwing, because "nothing
 * at any counter" is a real answer the screen has to show.
 */
export function pickPart<T extends Counter>(counters: T[]): {
  best: T | null; rest: T[];
} {
  const sorted = [...(counters ?? [])].sort((a, b) =>
    known(b) - known(a)
    || (a.supplierRank ?? 99) - (b.supplierRank ?? 99)
    || a.price - b.price);
  const [best, ...rest] = sorted;
  return { best: best ?? null, rest };
}
